/**
 * "Log in or sign up to claim" — the gate between preview and receipt
 * for an anonymous user.
 *
 * Why we don't just embed <LoginForm>/<RegisterForm>: those components
 * call `navigate('/dashboard')` on success. We need the user to STAY
 * on the redeem page so we can immediately fire the claim. So we drive
 * the login/register API surface directly here and let the parent
 * component (RedeemLanding) react when `user` flips truthy in the
 * shared auth store.
 *
 * OAuth (Google/Microsoft): the SocialLoginButtons component already
 * stores the JWT under localStorage and pushes the user into the
 * shared auth store via its own onSuccess callback. We re-use that —
 * the parent's auth-listening effect picks up the new user and kicks
 * off the claim. Before we let those buttons fire we stash the code
 * we're trying to redeem in sessionStorage in case the OAuth flow
 * round-trips through the provider's domain (Microsoft does;
 * Google's One-Tap stays in-page) — see RedeemLanding for the pickup.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { loginUser, registerUser } from '../../auth/api/authApi';
import { SocialLoginButtons } from '../../../components/auth';
import { OAuthError } from '../../../types/oauth';
import { User } from '../../../services/authService';

interface ClaimAuthStepProps {
  /** The code being redeemed — used to stash in sessionStorage before OAuth round-trips. */
  pendingCode: string;
  /** Fired after a successful in-page auth (login/register). The parent
   *  uses it to immediately POST /coupons/claim. */
  onAuthenticated: (user: User) => void;
}

type Mode = 'signin' | 'signup';

/** sessionStorage key the redeem flow reads on mount post-OAuth-bounce. */
export const PENDING_CLAIM_CODE_KEY = 'loxia.redeem.pendingClaimCode';

const ClaimAuthStep: React.FC<ClaimAuthStepProps> = ({
  pendingCode,
  onAuthenticated,
}) => {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthError, setOauthError] = useState<string | null>(null);

  // Persist the in-flight code so an OAuth round-trip (Microsoft, in
  // particular, may bounce through login.microsoftonline.com and land
  // back on our origin) can pick it up and continue the claim.
  const stashPendingCode = () => {
    try {
      window.sessionStorage.setItem(PENDING_CLAIM_CODE_KEY, pendingCode);
    } catch {
      // Storage disabled (Safari private mode etc) — fall through. The
      // worst case is the user has to manually re-enter the code after
      // signing in, which is the same UX as the no-code URL path.
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !password || (mode === 'signup' && (!firstName || !lastName))) {
      setError('Please fill in every field.');
      return;
    }
    setBusy(true);
    try {
      const user =
        mode === 'signup'
          ? await registerUser({ email, password, firstName, lastName })
          : await loginUser({ email, password });
      // The shared auth API already wrote the user into useAuthStore,
      // so the parent's effect will also fire. We pass user through
      // explicitly so the parent has a non-stale handle to it.
      onAuthenticated(user);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Authentication failed.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleOAuthSuccess = (user: User) => {
    onAuthenticated(user);
  };

  const handleOAuthError = (err: OAuthError) => {
    setOauthError(err.message || 'Sign-in failed.');
  };

  return (
    <Paper
      elevation={3}
      data-testid="claim-auth-step"
      sx={{ p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Typography variant="h5" component="h1">
        {mode === 'signin' ? 'Sign in to claim your reward' : 'Create an account to claim'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        We'll apply your coupon to this account the moment you're signed in.
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {mode === 'signup' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                required
                fullWidth
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={busy}
                inputProps={{ 'data-testid': 'auth-first-name' }}
              />
              <TextField
                required
                fullWidth
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={busy}
                inputProps={{ 'data-testid': 'auth-last-name' }}
              />
            </Stack>
          )}
          <TextField
            required
            fullWidth
            type="email"
            label="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            inputProps={{ 'data-testid': 'auth-email' }}
          />
          <TextField
            required
            fullWidth
            type="password"
            label="Password"
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            inputProps={{ 'data-testid': 'auth-password' }}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={busy}
            data-testid="auth-submit"
          >
            {busy ? (
              <CircularProgress size={24} />
            ) : mode === 'signin' ? (
              'Sign in & claim'
            ) : (
              'Create account & claim'
            )}
          </Button>
        </Stack>
      </Box>

      <Box textAlign="center">
        {mode === 'signin' ? (
          <Typography variant="body2">
            New here?{' '}
            <Link
              component="button"
              type="button"
              onClick={() => {
                setError(null);
                setMode('signup');
              }}
              data-testid="auth-switch-signup"
            >
              Create an account
            </Link>
          </Typography>
        ) : (
          <Typography variant="body2">
            Already have an account?{' '}
            <Link
              component="button"
              type="button"
              onClick={() => {
                setError(null);
                setMode('signin');
              }}
              data-testid="auth-switch-signin"
            >
              Sign in
            </Link>
          </Typography>
        )}
      </Box>

      {/* Stash the code BEFORE the OAuth buttons can navigate away. */}
      <Box onMouseDownCapture={stashPendingCode} onKeyDownCapture={stashPendingCode}>
        <SocialLoginButtons
          onSuccess={handleOAuthSuccess}
          onError={handleOAuthError}
          disabled={busy}
          context={mode}
          error={oauthError}
        />
      </Box>
    </Paper>
  );
};

export default ClaimAuthStep;
